import React, { useEffect, useRef, useState } from "react";
import { Button, Tabs, Tag, message } from "antd";
import { NextButton, PreviousButton } from "@comps/components";
import { makeRequestStateless } from "@utils/helpers";
import { getTimeSlotsReq } from "../requests";
import ReadinessSummary from "./ReadinessSummary";
import IndexTimeSlots from "@mods/rosterManagement/timeSlots/IndexTimeSlots";
import IndexCompanyRoaster from "@mods/rosterManagement/companyRoster/IndexCompanyRoaster";
import IndexAttendanceConfigurations from "@mods/attendanceManagement/settings/configurations/IndexAttendanceConfigurations";

// Step 3 — Shift Assignment. Reuses the real module screens in-place:
//   • Time Slots  -> IndexTimeSlots        (create/view/update/delete)
//   • Assign Shifts -> IndexCompanyRoaster (shifts table + create/update/delete/view)
//   • Attendance  -> IndexAttendanceConfigurations
// Gating: all three must be done before continuing (a slot exists, at least one
// shift is assigned, and attendance settings were saved). Completion flags are
// persisted into wizard.shifts so leaving/resuming restores them.

const extractSlotCount = (resp) => {
  const ts = resp?.time_slots || resp?.data?.time_slots;
  if (!ts) return 0;
  if (typeof ts.total === "number") return ts.total;
  if (Array.isArray(ts.data)) return ts.data.length;
  if (Array.isArray(ts)) return ts.length;
  return 0;
};

// The roster index reports its loaded shifts; the shape may be an array of
// shift rows or an object keyed by employee/date. Count entries defensively.
const countShifts = (shifts) => {
  if (!shifts) return 0;
  if (Array.isArray(shifts)) return shifts.length;
  if (typeof shifts === "object") {
    return Object.values(shifts).reduce((n, v) => {
      if (Array.isArray(v)) return n + v.length;
      return n + (v ? 1 : 0);
    }, 0);
  }
  return 0;
};

const StepShiftAssignment = (props) => {
  const { wizard, patch, onNext, onBack, readiness, readinessLoading, refreshReadiness, statuses, skipStep, clearSkip } = props;
  const saved = wizard.shifts || {};

  const [activeTab, setActiveTab] = useState("slots");
  const [slotCount, setSlotCount] = useState(saved.slotCount || readiness.shifts.timeSlotCount || 0);
  const [assignedCount, setAssignedCount] = useState(saved.assignedCount || readiness.shifts.assignedCount || 0);
  const [attendanceDone, setAttendanceDone] = useState(!!saved.attendanceDone || readiness.shifts.attendanceConfigured);

  const assignedDone = assignedCount > 0;

  // keep the latest values in a ref so persist() never writes stale siblings
  // (roster/attendance callbacks can close over an older render).
  const stateRef = useRef({});
  stateRef.current = { slotCount, assignedCount, attendanceDone };

  // best-effort persistence of completion flags into the wizard draft
  const persist = (over = {}) => {
    const cur = stateRef.current;
    patch({
      shifts: {
        slotCount: over.slotCount ?? cur.slotCount,
        assignedCount: over.assignedCount ?? cur.assignedCount,
        attendanceDone: over.attendanceDone ?? cur.attendanceDone,
      },
    });
  };

  const fetchSlotCount = (cb) => {
    makeRequestStateless(
      getTimeSlotsReq,
      { page: 1, filters: "" },
      (resp) => {
        const total = extractSlotCount(resp);
        setSlotCount(total);
        persist({ slotCount: total });
        cb?.(total);
      },
      () => cb?.(slotCount)
    );
  };

  useEffect(() => {
    fetchSlotCount();
    // eslint-disable-next-line
  }, []);

  // refresh the live slot count whenever the user leaves the Time Slots tab
  const onTabChange = (key) => {
    if (activeTab === "slots" && key !== "slots") fetchSlotCount();
    setActiveTab(key);
  };

  // fired by IndexCompanyRoaster every time it (re)loads its data — reflects
  // exactly what is in the shifts table after any create/update/delete.
  const onRosterData = (shifts) => {
    const count = countShifts(shifts);
    setAssignedCount(count);
    persist({ assignedCount: count });
  };

  const onAttendanceSaved = () => {
    setAttendanceDone(true);
    clearSkip?.("shifts");
    persist({ attendanceDone: true });
    refreshReadiness?.();
  };

  const proceed = () => {
    fetchSlotCount((total) => {
      persist({ slotCount: total });
      clearSkip?.("shifts");
      onNext();
    });
  };

  const skip = () => {
    skipStep?.("shifts");
    message.info("Shifts and attendance skipped for now. HR can finish this later.");
    onNext();
  };

  const doneTag = (ok) =>
    ok ? <Tag color="green">Done</Tag> : <Tag color="orange">Pending</Tag>;

  const items = [
    {
      key: "slots",
      label: <span>Time Slots {doneTag(slotCount > 0)}</span>,
      children: (
        <div className="onb-embed">
          <div className="onb-card-sub">
            Define your working shifts (start/end, buffers). You can create, view and edit them here.
          </div>
          <IndexTimeSlots modkey="timeSlot" />
        </div>
      ),
    },
    {
      key: "assign",
      label: <span>Assign Shifts {doneTag(assignedDone)}</span>,
      children: (
        <div className="onb-embed">
          <div className="onb-card-sub">
            Assign shifts to your employees using the roster below — create, edit, view or delete
            individual shifts. {assignedCount > 0 ? <><b>{assignedCount}</b> shift{assignedCount === 1 ? "" : "s"} in view.</> : null}
          </div>
          <IndexCompanyRoaster
            module="companyRoaster"
            filters=""
            modkey="roster"
            onDataChange={onRosterData}
          />
        </div>
      ),
    },
    {
      key: "attendance",
      label: <span>Attendance Settings {doneTag(attendanceDone)}</span>,
      children: (
        <div className="onb-embed">
          <div className="onb-card-sub">
            Configure how attendance is captured and validated, then Save.
          </div>
          <IndexAttendanceConfigurations modkey="attendanceConfigurations" onSaved={onAttendanceSaved} />
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="onb-card-title">Shift assignment</div>
      <div className="onb-card-sub">
        Optional: review shifts, assignments and attendance rules now, or skip them for later.
      </div>

      <ReadinessSummary
        status={statuses.shifts}
        loading={readinessLoading}
        onRefresh={refreshReadiness}
        items={[
          { label: "Time slots", value: readiness.shifts.timeSlotCount || slotCount || 0 },
          { label: "Assigned shifts", value: readiness.shifts.assignedCount || assignedCount || 0 },
          { label: "Attendance settings", value: readiness.shifts.attendanceConfigured || attendanceDone ? "Saved" : "Not saved" },
        ]}
      />

      <Tabs
        className="onb-tabs"
        activeKey={activeTab}
        onChange={onTabChange}
        items={items}
      />

      <div className="onb-foot">
        <PreviousButton onClick={onBack} text="Back" />
        <span className="spacer" />
        <Button onClick={skip}>Skip for now</Button>
        <NextButton onClick={proceed} text="Continue" />
      </div>
    </>
  );
};

export default StepShiftAssignment;
